/* Shared helpers for dashboard components */
import { poolData } from "../../../UserPool";
import { getApiUrl } from "../../commons";
import { uuidv4 } from "../../commons";
import { getProjectStore } from '../../../store/projectStore';

// Constants
export const DEFAULT_PROJECT_NAME = "Default";

/**
 * Switch active project and fetch its source environments.
 *
 * @param {string} pid - Project id to switch to
 * @param {{name?: string}} pData - Project data (at least name for messaging)
 * @param {string} [accessToken] - Optional token override; defaults to poolData.accessToken
 * @param {function} [setSnackMessage] - Optional setter for snack messages (currently unused)
 * @param {function} [reloadAllDashboard] - Optional callback to trigger dashboard reload (used for retry)
 * @param {number} [retryCount=0] - Internal retry counter
 */
export async function switchToProject(
  pid,
  pData,
  accessToken,
  setSnackMessage,
  reloadAllDashboard = () => {},
  retryCount = 0
) {
  try {
    await poolData.refreshToken();
  } catch (e) { /* noop */ }

  try {
    getProjectStore().setCurrentProject(pid);
    localStorage.lastProjectId = pid;
    localStorage.lastProjectIdAppliedMessage = `${pData?.name ? pData?.name : ""}`;
    localStorage.lastProjectName = pData?.name;
  } catch (e) { /* noop */ }

  try {
    getProjectStore().setCurrentSEnv(null);
    delete localStorage.lastSEnvId;
  } catch (e) { /* noop */ }

  try {
    const urlEnv = getApiUrl(poolData.envURL) + `/mgmt/environments/source/projects/${pid}`;
    const responseEnv = await fetch(urlEnv, {
      headers: {
        Authorization: `Bearer ${accessToken || poolData.accessToken} `,
      },
    });

    const jsonEnv = await responseEnv.json();

    const sEnvId = jsonEnv[0]?.id;
    getProjectStore().setCurrentSEnv(sEnvId);
    if (sEnvId) localStorage.lastSEnvId = sEnvId;

  } catch (e) {
    // env call failed, let's reload dash to refresh token,
    if (retryCount < 5) {
      try { reloadAllDashboard(); } catch (_) { /* noop */ }
      setTimeout(() => {
        switchToProject(pid, pData, accessToken, setSnackMessage, reloadAllDashboard, retryCount + 1);
      }, 2000);
    }
  }

  localStorage.needUpdate = JSON.stringify({
    discovery: true,
    multiverse: true,
    apps: true,
    assessments: true,
    factor: true,
    deploy: true,
    designer: true,
    scan: true,
  });

  console.log("switched to : ", pid, getProjectStore().currentSEnv, retryCount);
}

/**
 * Create default source environment for a project if missing.
 * Internal helper used by getProjects.
 */
export async function createSourceEnv(prj, accT) {
  const apiURL = getApiUrl(poolData.envURL);
  const eid = Math.floor(Math.random() * 9999);

  const bContent = JSON.stringify({
    name: `${prj.name}_SE`,
    environment_external_id: `${prj.name}_${eid}`,
    description: `${prj.name} - default source`,
    project_id: prj.id,
  });

  return fetch(apiURL + "/mgmt/environments/source", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accT}`,
      "Content-Type": "application/json",
    },
    body: bContent,
  });
}

/**
 * Fetch projects and ensure each has a source environment.
 */
export async function getProjects(accT) {
  const prjName = localStorage.prjName ? localStorage.prjName : "projects";
  const url = getApiUrl(poolData.envURL) + `/mgmt/${prjName}`;
  const accessToken = accT || poolData.accessToken;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }

    const json = await response.json();

    try {
      getProjectStore().setProjects(JSON.parse(JSON.stringify(json)));
    } catch (e) { /* noop */ }

    for (let i = 0; i < json.length; i++) {
      const prj = json[i];
      const urlEnv = getApiUrl(poolData.envURL) + `/mgmt/environments/source/${prjName}/${prj.id}`;

      const responseEnv = await fetch(urlEnv, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const jsonEnv = await responseEnv.json();
      if (jsonEnv.length === 0) {
        // create default source environment
        createSourceEnv(prj, accessToken);
      }
    }

    return json;
  } catch (e) {
    return {};
  }
}

/**
 * Delete a project and refresh project list.
 */
export async function deleteProject(id, poolDataRef = poolData, setProjectDivId) {
  await poolDataRef.refreshToken();
  const apiURL = getApiUrl(poolDataRef.envURL);

  try {
    const response = await fetch(apiURL + `/mgmt/projects/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${poolDataRef.accessToken}`,
      },
    });

    // drain body safely
    try { await response.text(); } catch (_) { /* noop */ }

    if (response.ok) {
      await getProjects();
      if (typeof setProjectDivId === 'function') {
        setTimeout(() => {
          try { setProjectDivId(uuidv4()); } catch (_) { /* noop */ }
        }, 420);
      }
      return true;
    }

    return false;
  } catch (error) {
    console.log("error", error);
    return false;
  }
}

/**
 * Create or update a project.
 */
export async function createProject({ id, name, is_default, description }, poolDataRef = poolData, setProjectDivId) {
  await poolDataRef.refreshToken();

  const apiURL = getApiUrl(poolDataRef.envURL);
  const eid = Math.floor(Math.random() * 9999);

  const { currentProjectId, currentSEnv } = getProjectStore();
  const bContent = id
    ? JSON.stringify({
        id,
        name,
        external_id: `${name}_${eid}`,
        description: description ? description : "",
        is_default: is_default,
        project_id: currentProjectId,
      })
    : JSON.stringify({
        name,
        external_id: `${name}_${eid}`,
        description: description ? description : "",
        is_default: false,
        project_id: currentProjectId,
      });

  try {
    const response = await fetch(apiURL + "/mgmt/projects" + (id ? "/" + id : ""), {
      method: id ? "PUT" : "POST",
      headers: {
        Authorization: `Bearer ${poolDataRef.accessToken}`,
        "Content-Type": "application/json",
        "x-project-id": currentProjectId || "",
        "x-environment-id": currentSEnv || "",
      },
      body: bContent,
    });

    // drain body safely
    try { await response.text(); } catch (_) { /* noop */ }

    if (response.ok) {
      await getProjects(poolDataRef.accessToken, true);

      // Dispatch event to notify components that project was updated
      if (id && id === currentProjectId) {
        localStorage.lastProjectName = name;
      }

      if (typeof setProjectDivId === 'function') {
        setTimeout(() => {
          try { setProjectDivId(uuidv4()); } catch (_) { /* noop */ }
        }, 420);
      }
      return true;
    }
    return false;
  } catch (error) {
    console.log('error', error);
    return false;
  }
}
